import type {Rule} from 'src/types/scheduler/templates';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu/Menu';

import {
  CONFLICT_PREVENTION_DATE_BASED,
  CONFLICT_PREVENTION_EVENT_BASED,
  CONFLICT_PREVENTION_EVENT_OUTSIDE_SENSE,
} from 'src/components/scheduler/template-editor/constants';


export type ParsedRule = Rule & {
  ruleId: number;
  isInvalid: boolean;
};

export const parseConflictPreventionRules = (rules: Rule[]): ParsedRule[] => {
  return rules.map((rule, index) => ({
    ...rule,
    ruleId: index,
    isInvalid: rule.value === '',
  }));
};

export const normalizeConflictPreventionRules = (
  rules: ParsedRule[],
): Rule[] => {
  return rules.map(({ruleId, isInvalid, ...rule}) => rule);
};

export const getNextUnavailableDate = (rules: ParsedRule[]): string => {
  let defaultDate = new Date().toISOString().split('T')[0]; // fallback to today

  const existingDates = rules
    .filter(
      (rule) => rule.ruleType === CONFLICT_PREVENTION_DATE_BASED && rule.value,
    )
    .map((rule) => new Date(rule.value))
    .filter((date) => !isNaN(date.getTime())); // filter out invalid dates

  if (existingDates.length > 0) {
    const highestDate = new Date(
      Math.max(...existingDates.map((date) => date.getTime())),
    );
    const nextDate = new Date(highestDate);
    nextDate.setDate(nextDate.getDate() + 1);
    defaultDate = nextDate.toISOString().split('T')[0];
  }

  return defaultDate;
};

export const getConflictPreventionOperatorOptions = (
  t: (key: string, value: string) => string,
): MenuOption[] => {
  return [
    {key: 'equals', label: `${t('equals', 'equals')}`},
    {key: 'contains', label: `${t('contains', 'contains')}`},
  ];
};

export const getConflictPreventionRuleTypeOptions = (
  t: (key: string, value: string) => string,
  hasEventOutsideSenseRule: boolean,
): MenuOption[] => {
  return [
    {
      key: CONFLICT_PREVENTION_EVENT_BASED,
      label: `${t('Event_Title', 'Event Title')}`,
    },
    {
      key: CONFLICT_PREVENTION_DATE_BASED,
      label: `${t('Specific_Date', 'Specific Date')}`,
    },
    {
      key: CONFLICT_PREVENTION_EVENT_OUTSIDE_SENSE,
      label: `${t('Events_not_booked_by_Sense', 'Events not booked by Sense')}`,
      disabled: hasEventOutsideSenseRule,
    },
  ];
};

export const CONFLICT_PREVENTION_RULE_TYPES = [
  CONFLICT_PREVENTION_DATE_BASED,
  CONFLICT_PREVENTION_EVENT_BASED,
  CONFLICT_PREVENTION_EVENT_OUTSIDE_SENSE,
] as const;
